import {
  adminRegistrationSchema,
  type AdminEvent,
  type AdminRegistration,
  type EventRole,
} from '@growup/site-shared';
import {
  Button,
  Card,
  Checkbox,
  Input,
  Notice,
  Select,
  StatusBadge,
  Table,
  type Column,
} from '@growup/ui';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { z } from 'zod';

import { download, getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';
import { matchesQuery } from '../quickFilter';
import { InviteForm } from './InviteForm';
import { formatEventDay, LEVEL_LABEL, REGISTRATION_LOOK, ROLE_LABEL, ROLE_PLURAL } from './labels';

type Filter = EventRole | 'pending' | 'invited' | 'all';

/** Ativa, ou convite por responder: aparece no roster sem "Mostrar cancelados". */
const isActive = (r: AdminRegistration) =>
  r.status === 'invited' ||
  r.status === 'pending' ||
  r.status === 'confirmed' ||
  r.status === 'waitlisted';

type Counts = AdminEvent['counts'];

/** `Visitantes 3/50 (+2 em espera) · Voluntários 1/4 (1 por aprovar) · ...` */
function countsLine(event: AdminEvent, c: Counts): string {
  return (
    `Visitantes ${String(c.visitor.confirmed)}` +
    (event.capacity === null ? '' : `/${String(event.capacity)}`) +
    (c.visitor.waitlisted > 0 ? ` (+${String(c.visitor.waitlisted)} em espera)` : '') +
    ` · Voluntários ${String(c.volunteer.confirmed)}` +
    (event.volunteerSlots === null ? '' : `/${String(event.volunteerSlots)}`) +
    (c.volunteer.pending > 0 ? ` (${String(c.volunteer.pending)} por aprovar)` : '') +
    ` · Streamers ${String(c.streamer.confirmed)}` +
    (event.streamerSlots === null ? '' : `/${String(event.streamerSlots)}`) +
    (c.streamer.pending > 0 ? ` (${String(c.streamer.pending)} por aprovar)` : '')
  );
}

/** Today in Lisbon, `aaaa-mm-dd`. */
const lisbonToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Lisbon' }).format(new Date());

/**
 * The roster: approvals for volunteers and streamers, check-in on each day,
 * CSV. A multi-day event can be filtered by day (decided by GrowuP, 19-10-2026);
 * capacity and slots count per day.
 */
export function RosterView({ event, onChanged }: { event: AdminEvent; onChanged: () => void }) {
  const [rows, setRows] = useState<AdminRegistration[] | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const multiDay = event.days.length > 1;
  // On a day of the event, start on that day: it is check-in time.
  const [day, setDay] = useState<string>(() => {
    const today = lisbonToday();
    return multiDay && event.days.some((d) => d.day === today) ? today : 'all';
  });
  const [showInactive, setShowInactive] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  // Convida-se ate o evento comecar, mesmo com as inscricoes fechadas.
  const canInvite = event.status === 'published' && new Date(event.startsAt).getTime() > Date.now();

  const load = useCallback(() => {
    getJson(`/api/site/events/${event.id}/registrations`, z.array(adminRegistrationSchema))
      .then(setRows)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [event.id]);
  useEffect(load, [load]);

  const act = async (r: AdminRegistration, path: string, body?: unknown) => {
    setBusyId(r.id);
    setError(null);
    try {
      await send('POST', `/api/site/events/${event.id}/registrations/${r.id}/${path}`, body);
      load();
      onChanged();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusyId(null);
    }
  };

  const dayOf = (r: AdminRegistration) => r.days.find((d) => d.day === day);
  const visible = (rows ?? []).filter((r) => {
    const declinedInvite = filter === 'invited' && r.status === 'declined';
    if (!showInactive && !isActive(r) && !declinedInvite) return false;
    if (
      !matchesQuery(
        query,
        r.person.login,
        r.person.email,
        r.guardian?.login,
        r.guardian?.email,
        r.streamer?.channel,
      )
    )
      return false;
    if (day !== 'all' && r.status === 'invited') {
      if (r.invite?.days && !r.invite.days.includes(day)) return false;
    } else if (day !== 'all') {
      const d = dayOf(r);
      if (d === undefined || (isActive(r) && d.status === 'cancelled')) return false;
    }
    if (filter === 'all') return true;
    if (filter === 'pending') return r.status === 'pending';
    if (filter === 'invited') return r.status === 'invited' || r.status === 'declined';
    return r.role === filter;
  });

  /** The days someone comes, with the waiting list on full days. */
  const daysText = (r: AdminRegistration) =>
    r.status === 'invited'
      ? r.invite?.days
        ? r.invite.days.map(formatEventDay).join(' · ')
        : 'Todos os dias'
      : r.days
          .filter((d) => d.status !== 'cancelled')
          .map((d) => {
            if (d.status !== 'waitlisted') return formatEventDay(d.day);
            const at = d.waitlistPosition === null ? '' : ` ${String(d.waitlistPosition)}.º`;
            return `${formatEventDay(d.day)} (espera${at})`;
          })
          .join(' · ');

  /** Check-in on the chosen day, or on each confirmed day when showing them all. */
  const checkIns = (r: AdminRegistration, busy: boolean): ReactNode =>
    r.days
      .filter((d) => d.status === 'confirmed' && (day === 'all' || d.day === day))
      .map((d) => (
        <Checkbox
          key={d.day}
          label={multiDay && day === 'all' ? `Presente ${formatEventDay(d.day)}` : 'Presente'}
          checked={d.checkedInAt !== null}
          disabled={busy}
          onChange={(e) => void act(r, 'check-in', { present: e.target.checked, day: d.day })}
        />
      ));

  const columns: Column<AdminRegistration>[] = [
    {
      key: 'person',
      header: 'Pessoa',
      cell: (r) => (
        <span className="flex flex-col">
          <span className="font-semibold">{r.person.login ?? '(conta removida)'}</span>
          <span className="text-caption text-gu-ink-muted">
            {r.person.isYouth
              ? `Menor · encarregado ${r.guardian?.login ?? '?'}${r.guardian?.email ? ` (${r.guardian.email})` : ''}`
              : (r.person.email ?? '')}
          </span>
          {r.note ? <span className="text-caption">“{r.note}”</span> : null}
        </span>
      ),
    },
    {
      key: 'role',
      header: 'Papel',
      cell: (r) => (
        <span className="flex flex-col">
          <span>{ROLE_LABEL[r.role]}</span>
          {r.streamer?.channel ? (
            <span className="text-caption text-gu-ink-muted">{r.streamer.channel}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'level',
      header: 'Nível',
      hideOnMobile: true,
      cell: (r) =>
        r.person.level === 'associate' ? (
          <StatusBadge tone="info">Associado</StatusBadge>
        ) : (
          (LEVEL_LABEL[r.person.level ?? ''] ?? '—')
        ),
    },
    {
      key: 'status',
      header: 'Estado',
      cell: (r) => {
        // A visitor's place is per day: on a chosen day, show that day's.
        const d = day === 'all' ? undefined : dayOf(r);
        const status =
          d !== undefined && r.role === 'visitor' && r.status !== 'cancelled' ? d.status : r.status;
        const position = d !== undefined ? d.waitlistPosition : r.waitlistPosition;
        return (
          <span className="flex flex-col gap-1">
            <StatusBadge tone={REGISTRATION_LOOK[status].tone}>
              {REGISTRATION_LOOK[status].label}
              {position !== null ? ` · ${String(position)}.º` : ''}
            </StatusBadge>
            {multiDay && day === 'all' ? <span className="text-caption">{daysText(r)}</span> : null}
            {r.invite ? (
              <span className="text-caption text-gu-ink-muted">
                Convidado por {r.invite.by ?? '?'} · {formatDateTime(r.invite.at)}
                {r.invite.answeredAt ? ` · respondeu ${formatDateTime(r.invite.answeredAt)}` : ''}
              </span>
            ) : (
              <span className="text-caption text-gu-ink-muted">{formatDateTime(r.createdAt)}</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: 'Ações',
      align: 'right',
      cell: (r) => {
        const busy = busyId === r.id;
        return (
          <span className="flex flex-wrap items-center justify-end gap-2">
            {r.role !== 'visitor' && (r.status === 'pending' || r.status === 'rejected') ? (
              <Button
                size="sm"
                disabled={busy}
                onClick={() => void act(r, 'decision', { decision: 'confirm', note: null })}
              >
                Aprovar
              </Button>
            ) : null}
            {r.role !== 'visitor' && (r.status === 'pending' || r.status === 'confirmed') ? (
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => {
                  const note = window.prompt('Nota para o email (opcional):', '');
                  if (note === null) return;
                  void act(r, 'decision', { decision: 'reject', note: note.trim() || null });
                }}
              >
                Recusar
              </Button>
            ) : null}
            {r.status === 'confirmed' ? checkIns(r, busy) : null}
            {r.status === 'invited' ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  if (window.confirm(`Cancelar o convite a ${r.person.login ?? 'esta pessoa'}?`))
                    void act(r, 'cancel');
                }}
              >
                Cancelar convite
              </Button>
            ) : isActive(r) ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => {
                  if (
                    window.confirm(
                      `Tirar ${r.person.login ?? 'esta pessoa'} do roster? Recebe um email.`,
                    )
                  )
                    void act(r, 'cancel');
                }}
              >
                Remover
              </Button>
            ) : null}
          </span>
        );
      },
    },
  ];

  const dayCounts = day === 'all' ? undefined : event.days.find((d) => d.day === day);
  return (
    <Card
      headingLevel={2}
      title="Roster"
      actions={
        <span className="flex flex-wrap gap-2">
          {canInvite ? (
            <Button size="sm" onClick={() => setInviting((v) => !v)}>
              Convidar
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              void download(
                `/api/site/events/${event.id}/roster.csv`,
                `roster-${event.slug}.csv`,
              ).catch((e: unknown) => setError(messageOf(e)))
            }
          >
            Exportar CSV
          </Button>
        </span>
      }
    >
      <div className="flex flex-col gap-3">
        {inviting && canInvite ? (
          <InviteForm
            event={event}
            onInvited={() => {
              load();
              onChanged();
            }}
            onClose={() => setInviting(false)}
          />
        ) : null}
        {multiDay ? (
          <ul className="flex flex-col text-gu-ink-muted">
            {event.days
              .filter((d) => day === 'all' || d.day === day)
              .map((d) => (
                <li key={d.day}>
                  <span className="font-semibold">{formatEventDay(d.day)}</span>:{' '}
                  {countsLine(event, d)}
                </li>
              ))}
          </ul>
        ) : (
          <p className="text-gu-ink-muted">{countsLine(event, dayCounts ?? event.counts)}</p>
        )}
        {multiDay ? (
          <p className="text-caption text-gu-ink-muted">
            A lotação e as vagas contam por dia. Cada pessoa escolhe os dias a que vem.
          </p>
        ) : null}
        <p className="text-caption text-gu-ink-muted">
          A prioridade dos associados é gerida à mão: o nível de cada pessoa aparece na lista.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Input
            type="search"
            aria-label="Procurar no roster"
            placeholder="Procurar login, email ou canal"
            className="max-w-xs"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {multiDay ? (
            <Select
              aria-label="Dia"
              className="max-w-xs"
              value={day}
              onChange={(e) => setDay(e.target.value)}
            >
              <option value="all">Todos os dias</option>
              {event.days.map((d) => (
                <option key={d.day} value={d.day}>
                  {formatEventDay(d.day)}
                </option>
              ))}
            </Select>
          ) : null}
          <Select
            aria-label="Mostrar"
            className="max-w-xs"
            value={filter}
            onChange={(e) => setFilter(e.target.value as Filter)}
          >
            <option value="all">Todos os papéis</option>
            <option value="pending">Por aprovar</option>
            <option value="invited">Convites</option>
            {(Object.keys(ROLE_PLURAL) as EventRole[]).map((role) => (
              <option key={role} value={role}>
                {ROLE_PLURAL[role]}
              </option>
            ))}
          </Select>
          <Checkbox
            label="Mostrar cancelados e recusados"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
        </div>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <Table
          caption={`Roster de ${event.title}`}
          columns={columns}
          rows={visible}
          rowKey={(r) => r.id}
          empty={rows ? 'Ninguém inscrito ainda.' : 'A carregar…'}
        />
      </div>
    </Card>
  );
}
